import { useEffect, useMemo, useRef, useState } from 'react';
import {
  byMonth,
  monthLabel,
  photoTimeLabel,
  PHOTO_RULES,
  type Photo,
  type PhotoAlbum,
} from '@td/shared';
import {
  photoDownloadUrl,
  photoFullUrl,
  photoThumbUrl,
  photoZipUrl,
  useCreateAlbum,
  useDeleteAlbum,
  useDeletePhoto,
  usePhotoBook,
  useRenameAlbum,
  useUpdatePhoto,
  useUploadPhoto,
} from '../hooks/usePhotos';
import { usePrivacy } from '../privacy/PrivacyContext';
import { HeaderMenu } from '../components/HeaderMenu';
import { Modal } from '../components/Modal';
import { Portal } from '../components/Portal';
import { useConfirm } from '../components/ConfirmDialog';
import { Doodle } from '../components/Doodle';

/**
 * The photo book.
 *
 * A year of a classroom, in months. Everything here is built around one fact:
 * these photographs are irreplaceable and the rest of the app is not. So the
 * original file is what is stored and what comes back out — the grid shows
 * small copies for speed, but download, print and slideshow all use the real
 * thing.
 *
 * Selecting is the spine of the page. Slideshow, print and download all act on
 * "what is selected, or everything if nothing is", which means there is one
 * idea to learn rather than three buttons with three scopes.
 */

type PrintMode = 'sheet' | 'pages';

export function PhotosPage() {
  /** Null is the year in progress; a label is one that has been put away. */
  const [year, setYear] = useState<string | null>(null);
  const [album, setAlbum] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /*
   * The open photo is held by id, not as a copy of it.
   *
   * Holding the object meant the card was a snapshot from the moment it was
   * opened: choosing an album saved fine and refetched fine, but the dropdown
   * went on showing the old value until the card was closed and reopened,
   * because the card was still looking at the copy it started with.
   */
  const [showingId, setShowingId] = useState<string | null>(null);
  const [slideshow, setSlideshow] = useState<Photo[] | null>(null);
  const [printing, setPrinting] = useState<{ photos: Photo[]; mode: PrintMode } | null>(null);
  const [managing, setManaging] = useState(false);

  const { data, isLoading } = usePhotoBook(year);
  const upload = useUploadPhoto();
  const remove = useDeletePhoto();
  const confirm = useConfirm();

  const fileInput = useRef<HTMLInputElement>(null);
  const readOnly = year !== null;

  const photos = useMemo(() => {
    const all = data?.photos ?? [];
    return album ? all.filter((p) => p.albumId === album) : all;
  }, [data, album]);

  const months = useMemo(() => byMonth(photos), [photos]);

  // Always the live one. If it has just been deleted, the card closes itself.
  const showing = useMemo(
    () => (data?.photos ?? []).find((p) => p.id === showingId) ?? null,
    [data, showingId],
  );

  /*
   * What a button acts on: the selection, or everything on screen when nothing
   * is picked. One rule for slideshow, print and download.
   */
  const acting = useMemo(
    () => (selected.size > 0 ? photos.filter((p) => selected.has(p.id)) : photos),
    [photos, selected],
  );

  // A selection made in one album means nothing in another.
  useEffect(() => setSelected(new Set()), [album, year]);

  function toggle(id: string) {
    setSelected((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;

    /*
     * One at a time, on purpose. Twenty full-size photographs uploaded at
     * once is twenty simultaneous re-encodes on a small container, and the
     * whole batch fails together. Sequential is slower to watch and far more
     * likely to finish.
     */
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, albumId: album });
      } catch {
        // Keep going: one unreadable file should not stop the other nineteen.
      }
    }

    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <div className={`space-y-4 ${printing ? 'print:hidden' : ''}`}>
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1 className="flex items-baseline gap-2 text-2xl">
          Photo book
          {/* The only thing the header says about the selection: how many. */}
          {selected.size > 0 && (
            <span className="text-sm text-stone">{selected.size} selected</span>
          )}
        </h1>

        <div className="flex flex-wrap items-center gap-1.5">
          {(data?.archived.length ?? 0) > 0 && (
            <select
              className="field w-auto py-1.5 text-sm"
              value={year ?? ''}
              onChange={(e) => setYear(e.target.value || null)}
              aria-label="Which year"
            >
              <option value="">This year</option>
              {data!.archived.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
          )}

          {!readOnly && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept={PHOTO_RULES.acceptedMimeTypes.join(',')}
                multiple
                className="hidden"
                onChange={(e) => void onFiles(e.target.files)}
              />
              <button
                className="btn-primary"
                onClick={() => fileInput.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? 'Adding…' : 'Add photos'}
              </button>
            </>
          )}

          {/*
            Everything a page does, behind one button.

            These used to be a second bar of their own under the header, which
            read as two headers stacked on a page whose whole job is to show
            photographs. What each one acts on is written on it, so the scope
            is never a guess: the selection when there is one, everything on
            screen when there is not.
          */}
          <HeaderMenu label="What to do with these" width={280}>
            {(close) => {
              const scope = selected.size > 0 ? `the ${selected.size} selected` : 'all of these';
              const act = (run: () => void) => () => {
                run();
                close();
              };

              return (
                <div className="space-y-1.5">
                  <p className="px-1 pb-1 text-[11px] text-stone">
                    {photos.length === 0
                      ? 'Nothing here yet'
                      : selected.size > 0
                        ? `${selected.size} selected`
                        : `${photos.length} photo${photos.length === 1 ? '' : 's'}`}
                  </p>

                  <button
                    className="btn-soft w-full"
                    disabled={photos.length === 0}
                    onClick={act(() => setSlideshow(acting))}
                  >
                    Slideshow of {scope}
                  </button>

                  <button
                    className="btn-soft w-full"
                    disabled={photos.length === 0}
                    onClick={act(() => setPrinting({ photos: acting, mode: 'sheet' }))}
                  >
                    Print {scope} on a sheet
                  </button>

                  <button
                    className="btn-soft w-full"
                    disabled={photos.length === 0}
                    onClick={act(() => setPrinting({ photos: acting, mode: 'pages' }))}
                  >
                    Print {scope} full size
                  </button>

                  <a
                    className="btn-soft block w-full text-center"
                    href={
                      selected.size > 0
                        ? photoZipUrl({ year, ids: [...selected] })
                        : photoZipUrl({ year, albumId: album ?? undefined })
                    }
                    onClick={close}
                  >
                    Download {selected.size > 0 ? scope : `the whole ${album ? 'album' : 'year'}`}
                  </a>

                  <div className="!mt-2 border-t border-sand pt-2" />

                  {selected.size > 0 ? (
                    <button className="btn-soft w-full" onClick={act(() => setSelected(new Set()))}>
                      Clear the selection
                    </button>
                  ) : (
                    <button
                      className="btn-soft w-full"
                      disabled={photos.length === 0}
                      onClick={act(() => setSelected(new Set(photos.map((p) => p.id))))}
                    >
                      Select every one of these
                    </button>
                  )}

                  <button
                    className="btn-soft w-full"
                    disabled={readOnly}
                    onClick={act(() => setManaging(true))}
                  >
                    Albums
                  </button>

                  {selected.size > 0 && !readOnly && (
                    <button
                      className="btn-soft-danger w-full"
                      onClick={async () => {
                        close();
                        const ok = await confirm({
                          title: `Delete ${selected.size} photo${selected.size === 1 ? '' : 's'}?`,
                          body: 'They cannot be got back. Download them first if you are not sure.',
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (!ok) return;

                        for (const id of selected) await remove.mutateAsync(id);
                        setSelected(new Set());
                      }}
                    >
                      Delete the {selected.size} selected
                    </button>
                  )}
                </div>
              );
            }}
          </HeaderMenu>
        </div>
      </header>

      {readOnly && (
        <p className="rounded-card border border-sand bg-linen/60 px-3 py-2 text-sm text-walnut">
          This is {year}, put away at the end of that year. You can look at it, print it and
          download it — nothing here can be changed.
        </p>
      )}

      {/* ------------------------------------------------------- the albums */}
      {(data?.albums.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={() => setAlbum(null)} className={album === null ? 'btn-primary' : 'btn-soft'}>
            Everything
          </button>
          {data!.albums.map((a) => (
            <button
              key={a.id}
              onClick={() => setAlbum(a.id)}
              className={album === a.id ? 'btn-primary' : 'btn-soft'}
            >
              {a.name}
              <span className="ml-1.5 opacity-70">{a.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ------------------------------------------------------- the months */}
      {isLoading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : photos.length === 0 ? (
        <div className="rounded-card border border-sand bg-white p-8 text-center">
          <Doodle name="star" className="mx-auto mb-2 h-10 w-10 text-clay" />
          <p className="text-sm text-stone">
            {readOnly
              ? 'There are no photos in this year.'
              : 'No photos yet. Add some, and they will sort themselves into months.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {months.map(({ month, photos: inMonth }) => (
            <section key={month}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 border-b border-sand pb-1">
                <h2 className="text-lg">{monthLabel(month)}</h2>

                <span className="flex items-center gap-2 text-xs text-stone">
                  {inMonth.length} photo{inMonth.length === 1 ? '' : 's'}
                  <a className="btn-soft px-2 py-0.5" href={photoZipUrl({ year, month })}>
                    Download the month
                  </a>
                </span>
              </div>

              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {inMonth.map((photo) => (
                  <li key={photo.id}>
                    <PhotoTile
                      photo={photo}
                      picked={selected.has(photo.id)}
                      onPick={() => toggle(photo.id)}
                      onOpen={() => setShowingId(photo.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {showing && (
        <PhotoCard
          // Keyed by id, so opening a different photo starts its own card
          // rather than reusing the last one's half-typed name.
          key={showing.id}
          photo={showing}
          albums={data?.albums ?? []}
          readOnly={readOnly}
          onClose={() => setShowingId(null)}
        />
      )}

      {managing && <AlbumManager albums={data?.albums ?? []} onClose={() => setManaging(false)} />}

      {slideshow && slideshow.length > 0 && (
        <Slideshow photos={slideshow} onClose={() => setSlideshow(null)} />
      )}

      {printing && <PrintSheet {...printing} onDone={() => setPrinting(null)} />}
    </div>
  );
}

/* ---------------------------------------------------------------- a tile */

function PhotoTile({
  photo,
  picked,
  onPick,
  onOpen,
}: {
  photo: Photo;
  picked: boolean;
  onPick: () => void;
  onOpen: () => void;
}) {
  const { privacy } = usePrivacy();

  return (
    <div
      className={[
        'group relative overflow-hidden rounded-card border-2 bg-white transition-colors',
        picked ? 'border-terracotta' : 'border-sand',
      ].join(' ')}
    >
      <button className="block w-full" onClick={onOpen} aria-label={photo.title || 'Open the photo'}>
        <img
          src={photoThumbUrl(photo)}
          alt={photo.title}
          loading="lazy"
          draggable={false}
          className={[
            'aspect-square w-full object-cover transition-[filter] duration-200',
            // Same rule as the roster photos: blurred, not hidden, so the page
            // still reads when the room is looking at the projector.
            privacy ? 'blur-lg' : '',
          ].join(' ')}
        />
      </button>

      {/* The picker sits over the corner rather than under the photo, so a
          grid of forty photos is still a grid of forty photos. */}
      <label
        className="absolute left-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-sand bg-cream/90"
        title={picked ? 'Selected' : 'Select'}
      >
        <input type="checkbox" checked={picked} onChange={onPick} className="h-3.5 w-3.5" />
      </label>

      <div className="px-2 py-1.5">
        <p className="truncate text-xs text-espresso">{photo.title || 'Untitled'}</p>
        <p className="truncate text-[11px] text-stone">{photoTimeLabel(photo.takenAt)}</p>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- a photo */

function PhotoCard({
  photo,
  albums,
  readOnly,
  onClose,
}: {
  photo: Photo;
  albums: PhotoAlbum[];
  readOnly: boolean;
  onClose: () => void;
}) {
  const update = useUpdatePhoto();
  const remove = useDeletePhoto();
  const confirm = useConfirm();
  const { privacy } = usePrivacy();

  const [title, setTitle] = useState(photo.title);

  /** 'YYYY-MM-DDTHH:mm' — what a datetime-local input wants. */
  const localValue = useMemo(() => {
    const date = new Date(photo.takenAt);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours(),
    )}:${pad(date.getMinutes())}`;
  }, [photo.takenAt]);

  return (
    <Modal
      onClose={onClose}
      label={photo.title || 'Photo'}
      className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-card bg-cream p-4 shadow-lifted"
    >
      <img
        src={photoFullUrl(photo)}
        alt={photo.title}
        className={`mx-auto max-h-[62vh] rounded-card object-contain ${privacy ? 'blur-lg' : ''}`}
      />

      <div className="mt-3 space-y-2">
        {readOnly ? (
          <div>
            <h2 className="text-lg">{photo.title || 'Untitled'}</h2>
            <p className="text-xs text-stone">{photoTimeLabel(photo.takenAt)}</p>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto]">
            <label className="block">
              <span className="label">Name</span>
              <input
                className="field"
                value={title}
                maxLength={120}
                placeholder="The 100th day"
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => {
                  if (title !== photo.title) update.mutate({ id: photo.id, title });
                }}
              />
            </label>

            <label className="block">
              <span className="label">
                When {photo.dateFromCamera && <span className="text-stone">· from the camera</span>}
              </span>
              <input
                type="datetime-local"
                className="field"
                value={localValue}
                onChange={(e) => {
                  const next = new Date(e.target.value);
                  if (!Number.isNaN(next.getTime())) {
                    update.mutate({ id: photo.id, takenAt: next.toISOString() });
                  }
                }}
              />
            </label>

            <label className="block">
              <span className="label">Album</span>
              <select
                className="field"
                value={photo.albumId ?? ''}
                onChange={(e) => update.mutate({ id: photo.id, albumId: e.target.value || null })}
              >
                <option value="">None</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        <p className="text-[11px] text-stone">
          {photo.width} × {photo.height} · {(photo.size / 1024 / 1024).toFixed(1)} MB
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <a className="btn-primary" href={photoDownloadUrl(photo)}>
          Download
        </a>

        {!readOnly && (
          <button
            className="btn-soft-danger"
            onClick={async () => {
              const ok = await confirm({
                title: `Delete ${photo.title || 'this photo'}?`,
                body: 'It cannot be got back. Download it first if you are not sure.',
                confirmLabel: 'Delete',
                danger: true,
              });
              if (ok) {
                remove.mutate(photo.id);
                onClose();
              }
            }}
          >
            Delete
          </button>
        )}

        <button className="btn-secondary ml-auto" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------- albums */

function AlbumManager({ albums, onClose }: { albums: PhotoAlbum[]; onClose: () => void }) {
  const create = useCreateAlbum();
  const rename = useRenameAlbum();
  const remove = useDeleteAlbum();
  const confirm = useConfirm();

  const [name, setName] = useState('');

  return (
    <Modal
      onClose={onClose}
      label="Albums"
      className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-card bg-cream p-5 shadow-lifted"
    >
      <h2 className="mb-3 text-lg">Albums</h2>

      <ul className="space-y-2">
        {albums.map((album) => (
          <li key={album.id} className="flex items-center gap-2">
            <input
              className="field flex-1 py-1 text-sm"
              defaultValue={album.name}
              maxLength={80}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next && next !== album.name) rename.mutate({ id: album.id, name: next });
              }}
              aria-label="Album name"
            />
            <span className="w-12 text-right text-xs text-stone">{album.count}</span>
            <button
              className="btn-soft-danger px-2 py-1"
              onClick={async () => {
                const ok = await confirm({
                  title: `Delete the ${album.name} album?`,
                  body: 'The photos stay in the book — they just stop being a group.',
                  confirmLabel: 'Delete',
                  danger: true,
                });
                if (ok) remove.mutate(album.id);
              }}
            >
              Delete
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-end gap-2">
        <label className="flex-1">
          <span className="label">A new album</span>
          <input
            className="field py-1"
            value={name}
            maxLength={80}
            placeholder="Field trip"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <button
          className="btn-primary"
          disabled={!name.trim() || create.isPending}
          onClick={() => create.mutate(name.trim(), { onSuccess: () => setName('') })}
        >
          Add it
        </button>
      </div>

      <div className="mt-4">
        <button className="btn-secondary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ slideshow */

/**
 * The photos, one at a time, big.
 *
 * Arrow keys and a tap on either side, and Escape to leave — the same three
 * gestures every slideshow anyone has used has, because this is the one place
 * in the app where the room is watching and nobody can read a tooltip.
 */
function Slideshow({ photos, onClose }: { photos: Photo[]; onClose: () => void }) {
  const [at, setAt] = useState(0);
  const [playing, setPlaying] = useState(true);

  const photo = photos[Math.min(at, photos.length - 1)]!;

  const go = (by: number) => setAt((was) => (was + by + photos.length) % photos.length);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight' || e.key === ' ') go(1);
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'Escape') onClose();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => go(1), 5000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, photos.length]);

  return (
    <Portal>
      {/*
        Its own backdrop rather than Modal's, because a slideshow is meant to
        fill the screen in a dark room. It closes on a click on the backdrop
        and on Escape, which is the rule everything that pops out follows.
      */}
      <div
        className="fixed inset-0 z-50 flex flex-col bg-espresso/95"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="flex items-center justify-between gap-3 p-3 text-cream">
          <div className="min-w-0">
            <p className="truncate text-sm">{photo.title || 'Untitled'}</p>
            <p className="text-xs opacity-70">
              {photoTimeLabel(photo.takenAt)} · {at + 1} of {photos.length}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <button className="btn-soft" onClick={() => setPlaying(!playing)}>
              {playing ? 'Pause' : 'Play'}
            </button>
            <button className="btn-soft" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2 pb-4">
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-cream/20 px-3 py-6 text-2xl text-cream"
            onClick={() => go(-1)}
            aria-label="The one before"
          >
            ‹
          </button>

          {/*
            The full-size file, not the thumbnail. A slideshow on the
            classroom projector is exactly the moment the real photograph is
            wanted, and the privacy screen is deliberately not applied here —
            she opened this on purpose, in front of the people in it.
          */}
          <img
            src={photoFullUrl(photo)}
            alt={photo.title}
            className="max-h-full max-w-full object-contain"
          />

          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-cream/20 px-3 py-6 text-2xl text-cream"
            onClick={() => go(1)}
            aria-label="The next one"
          >
            ›
          </button>
        </div>
      </div>
    </Portal>
  );
}

/* --------------------------------------------------------------- print */

/**
 * Printed through a portal, never shown on screen.
 *
 * A contact sheet is a page of small photos with their names — the memory
 * book page. Full size is one photograph to a sheet. Both wait a moment for
 * the images to load before the print dialog opens, because a print of an
 * `<img>` that has not decoded yet is a page of blank rectangles.
 */
function PrintSheet({
  photos,
  mode,
  onDone,
}: {
  photos: Photo[];
  mode: PrintMode;
  onDone: () => void;
}) {
  const [ready, setReady] = useState(0);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;

    // Every image loaded, or two seconds — whichever comes first. A single
    // slow photo must not leave her looking at a page that never prints.
    const enough = ready >= photos.length;
    const id = window.setTimeout(
      () => {
        fired.current = true;
        window.print();
        onDone();
      },
      enough ? 120 : 2000,
    );

    return () => window.clearTimeout(id);
  }, [ready, photos.length, onDone]);

  const count = () => setReady((n) => n + 1);

  return (
    <Portal>
      <div className="hidden print:block">
        {mode === 'sheet' ? (
          <div className="print-sheet bg-white p-6">
            <div className="grid grid-cols-3 gap-4">
              {photos.map((photo) => (
                <figure key={photo.id} className="break-inside-avoid">
                  <img
                    src={photoFullUrl(photo)}
                    alt=""
                    onLoad={count}
                    onError={count}
                    className="h-40 w-full rounded object-cover"
                  />
                  <figcaption className="mt-1 text-[10px] leading-tight">
                    {photo.title || 'Untitled'}
                    <span className="block text-stone">{photoTimeLabel(photo.takenAt)}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        ) : (
          photos.map((photo) => (
            <div key={photo.id} className="break-after-page bg-white p-6 text-center">
              <img
                src={photoFullUrl(photo)}
                alt=""
                onLoad={count}
                onError={count}
                className="mx-auto max-h-[9in] max-w-full object-contain"
              />
              <p className="mt-2 text-sm">
                {photo.title || ''}
                {photo.title && ' · '}
                {photoTimeLabel(photo.takenAt)}
              </p>
            </div>
          ))
        )}
      </div>
    </Portal>
  );
}
